import type { ZudokuConfig } from "zudoku";
import { HomePage } from "./src/HomePage";
import { XmlFormatter } from "./components/XmlFormatter";

const config: ZudokuConfig = {
  // --- SITE METADATA AND SEO ---
  // Added from the example for better SEO and browser tab titles.
  metadata: {
    title: "%s | Gracenote Documentation",
    showPoweredBy: false, // Line to hide the link
    defaultTitle: "Gracenote Documentation",
    favicon: "/favicon.svg", // Primary favicon using SVG for better quality
  },
  
  // --- THEME AND APPEARANCE ---
  theme: {
    registryUrl: "https://tweakcn.com/r/themes/cmegq77hk000004l1aa95b36o",
    colors: {
      // Brand colors
      primary: "#2563eb",
      secondary: "#64748b",
      
      // Syntax highlighting colors
      "syntax-keyword": "#2563eb", // Keywords in brand blue
      "syntax-string": "#059669", // Strings in green
      "syntax-comment": "#64748b", // Comments in gray
      "syntax-number": "#dc2626", // Numbers in red
      "syntax-function": "#7c3aed", // Functions in purple
      "syntax-variable": "#ea580c", // Variables in orange
      
      // Markdown syntax highlighting
      "markdown-heading": "#2563eb", // Headings in brand blue
      "markdown-bold": "#1f2937", // Bold text in dark gray
      "markdown-italic": "#6b7280", // Italic text in gray
      "markdown-link": "#2563eb", // Links in brand blue
      "markdown-code": "#dc2626", // Inline code in red
      "markdown-blockquote": "#64748b", // Blockquotes in gray
    },
  },
  
  // --- FONTS ---
  fonts: {
    sans: "Inter",
    mono: "JetBrains Mono",
  },

  // --- AUTHENTICATION DISABLED FOR API EXPLORER ---
  // Authentication disabled to provide standard API Explorer experience
  // without requiring user login, similar to Stoplight, Theneo, GitHub
  // authentication: {
  //   type: "auth0",
  //   domain: "dev-vg7o3jxrzj87n6qf.us.auth0.com",
  //   clientId: "iun14PisGjFa3YkVXb5rym4aIlVj0KeQ",
  // },

  // --- SITE BRANDING ---
  site: {
    title: "Gracenote Documentation",
    showPoweredBy: false,
    logo: {
      src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
      alt: "Gracenote",
      width: "130px",
      href: "/",
    },
    poweredBy: "",
    customCss: "/table-wrap.css",
    head: `
      <script defer src="https://cloud.umami.is/script.js" data-website-id="fdcccd31-5c59-465f-974f-db8bab73a8a6"></script>
      <meta name="robots" content="index, follow">
      <meta name="googlebot" content="index, follow">
      <meta name="bingbot" content="index, follow">
      <meta name="description" content="Official Gracenote API documentation for video, audio, sports, and automotive metadata services. Comprehensive guides, code examples, and API references.">
      <link rel="alternate" type="text/plain" href="/llms.txt">
    `,

    footer: {
      position: "center",
      copyright: `© ${new Date().getFullYear()} Gracenote LLC. All rights reserved.`,
      logo: {
        src: {
          light: "/logo-light.svg",
          dark: "/logo-dark.svg"
        },
        alt: "Gracenote Logo",
        width: "120px"
      }
    },
  },

  // --- DOCUMENTATION PAGE DEFAULTS ---
  docs: {
    defaultOptions: {
      showLastModified: true,
      copyPage: false,
    },
  },

  // --- MDX CONFIGURATION ---
  mdx: {
    remarkPlugins: [],
    rehypePlugins: [],
  },



  // --- SEARCH CONFIGURATION ---
  search: {
    type: "pagefind",
    // Optional: Maximum number of sub results per page
    maxSubResults: 3,
    // Optional: Configure search result ranking (defaults shown below)
    ranking: {
      termFrequency: 0.8,
      pageLength: 0.6,
      termSimilarity: 1.2,
      termSaturation: 1.2,
    },
  },
  
  // --- NAVIGATION ---
  navigation: [
    // Base navigation items
    {
      type: "custom-page",
      path: "/",
      element: <HomePage />,
    },
    {
      type: "link",
      to: "/",
      label: "Home",
      icon: "sparkles",
    },
    {
      type: "category",
      label: "Video",
      icon: "video",
      items: [
        "/video/index",
        {
          type: "category",
          label: "Video MCP Server",
          icon: "server",
          collapseTableOfContents: false,
          items: [
            "/video/video-mcp-server/intro",
            "/video/video-mcp-server/mcp-enabled-agent-architecture",
            "/video/video-mcp-server/gracenote-mcp-demo",
            "/video/video-mcp-server/mcp-client-authentication",
            "/video/video-mcp-server/system-prompt",
            "/video/video-mcp-server/mcp-tools",
            "/video/video-mcp-server/faq",
          ],
        },
        {
          type: "category",
          label: "On API",
          icon: "code",
          items: [
            "/video/on-api/about-the-on-api",
            {
              type: "category",
              label: "API Data Delivery",
              items: [
                "/video/on-api/getting-updates",
                "/video/on-api/getting-seed-files",
                "/video/on-api/system-requirements",
                {
                  type: "category",
                  label: "Images (Media Cloud)",
                  items: [
                    "/video/on-api/images-media-cloud",
                    "/video/on-api/image-types",
                  ],
                },
              ],
            },
            "/video/on-api/implementation",
            "/video/on-api/product-demonstration-kit",
            {
              type: "category",
              label: "Endpoints Overview",
              items: [
                "/video/on-api/endpoint-overview",
                {
                  type: "category",
                  label: "Linear Schedules (Unbound) Endpoints",
                  items: [
                    "/video/on-api/lineups-endpoint",
                    "/video/on-api/sources-endpoint",
                    "/video/on-api/schedules-endpoint",
                    "/video/on-api/programs-endpoint",
                    "/video/on-api/celebrities-endpoint",
                  ],
                },
                {
                  type: "category",
                  label: "Streaming Video (On Demand) Endpoints",
                  items: [
                    "/video/on-api/program-availabilities-endpoint",
                    "/video/on-api/program-mappings-endpoint",
                  ],
                },
                {
                  type: "category",
                  label: "Advanced Discovery Endpoints",
                  items: [
                    "/video/on-api/program-annotations-endpoint",
                    "/video/on-api/video-descriptors-taxonomy-endpoint",
                    "/video/on-api/video-popularity-endpoint",
                  ],
                },
                {
                  type: "category",
                  label: "On Sports Endpoints (Subscription Only)",
                  items: [
                    "/video/on-api/onsports-overview",
                    "/video/on-api/sports-endpoint",
                    "/video/on-api/sportsevents-endpoint",
                    "/video/on-api/teams-endpoint",
                    "/video/on-api/universities-endpoint",
                    "/video/on-api/venues-endpoint",
                    "/video/on-api/organizations-endpoint",
                  ],
                },
                "/video/on-api/gnids-sourceprograms-endpoint",
              ],
            },
            "/video/on-api/on-api-example-responses",
            {
              type: "link",
              to: "https://documentation.gracenote.com/on-api-downloads/on-api-schema-docs.html",
              label: "Schema Documentation",
              target: "_blank",
            },
            "/video/on-api/xml-schema-files",
            "/video/on-api/data-dictionaries-controlled-vocabulary",
            "/video/on-api/release-notes",
            {
              type: "link",
              to: "/catalog/on-api",
              label: "API Documentation",
            },
          ],
        },
        {
          type: "category",
          label: "GVD",
          icon: "database",
          items: [
            "/video/GVD/overview",
            {
              type: "category",
              label: "Getting Seed Data and Updates",
              items: [
                "/video/GVD/getting-seed-data-and-updates/getting-initial-data-and-updates",
                "/video/GVD/getting-seed-data-and-updates/handling-deleted-and-inactive-endpoint-records",
                {
                  type: "category",
                  label: "Getting Images and Updates",
                  items: [
                    "/video/GVD/getting-seed-data-and-updates/getting-images-and-updates",
                    "/video/GVD/getting-seed-data-and-updates/getting-images-and-updates/image-selection-guidelines",
                    "/video/GVD/getting-seed-data-and-updates/getting-images-and-updates/image-controlled-vocabulary",
                  ],
                },
              ],
            },
            {
              type: "category",
              label: "Implementation",
              items: [
                "/video/GVD/implementation/data-delivery-model",
                "/video/GVD/implementation/using-country-language-and-source-contexts",
                "/video/GVD/implementation/country-cvs-gnids-and-xids",
                "/video/GVD/implementation/working-with-program-titles",
                "/video/GVD/implementation/navigating-episodes-seasons-and-series",
                "/video/GVD/implementation/getting-channel-locations",
                {
                  type: "category",
                  label: "Transport Systems and Triplet Types",
                  items: [
                    "/video/GVD/implementation/transport-systems-and-triplet-types/transport-ids",
                    "/video/GVD/implementation/transport-systems-and-triplet-types/dvb-triplets",
                  ],
                },
                {
                  type: "category",
                  label: "Working with Japanese Data",
                  items: [
                    "/video/GVD/implementation/working-with-japanese-data/channel-locator-regions-for-japan",
                  ],
                },
                {
                  type: "category",
                  label: "Building a Programs Database",
                  items: [
                    "/video/GVD/implementation/building-a-programs-database/database-space-recommendations",
                    "/video/GVD/implementation/building-a-programs-database/mapping-gnids-to-primary-database-keys",
                    "/video/GVD/implementation/building-a-programs-database/use-case-1-extracting-programs-in-tv-channel-schedules",
                    "/video/GVD/implementation/building-a-programs-database/use-case-2-build-a-programs-db-from-gvd-api",
                    "/video/GVD/implementation/building-a-programs-database/use-case-3-building-an-ott-vod-program-guide",
                    "/video/GVD/implementation/building-a-programs-database/frequently-asked-questions",
                  ],
                },
              ],
            },
            {
              type: "category",
              label: "GVD Endpoints",
              items: [
                "/video/GVD/gvd-endpoints/using-the-gvd-api-explorer",
                {
                  type: "category",
                  label: "Advanced Discovery and Video Descriptors",
                  items: [
                    "/video/GVD/gvd-endpoints/advanced-discovery-and-video-descriptors/video-descriptor-coverage",
                    "/video/GVD/gvd-endpoints/advanced-discovery-and-video-descriptors/accessing-video-descriptors",
                    "/video/GVD/gvd-endpoints/advanced-discovery-and-video-descriptors/video-descriptor-weights",
                    "/video/GVD/gvd-endpoints/advanced-discovery-and-video-descriptors/video-descriptors-types",
                    "/video/GVD/gvd-endpoints/advanced-discovery-and-video-descriptors/video-descriptor-taxonomy-endpoint",
                    "/video/GVD/gvd-endpoints/advanced-discovery-and-video-descriptors/video-descriptor-taxonomy-items",
                  ],
                },
                "/video/GVD/gvd-endpoints/video-popularity",
                "/video/GVD/gvd-endpoints/video-on-demand-vod",
                "/video/GVD/gvd-endpoints/program-presentation-endpoint",
                {
                  type: "category",
                  label: "Endpoint Example Outputs",
                  items: [
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/availability-manifest-example-output",
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/availability-day-example-output",
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/catalog-manifest-example-output",
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/catalog-item-example-output",
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/contributor-context-example-output",
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/country-example-output",
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/lineup-example-output",
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/media-context-example-output",
                    {
                      type: "category",
                      label: "Program Context Examples",
                      items: [
                        "/video/GVD/gvd-endpoints/endpoint-example-outputs/program-context-examples/episode-example-output",
                        "/video/GVD/gvd-endpoints/endpoint-example-outputs/program-context-examples/movie-example-output",
                        "/video/GVD/gvd-endpoints/endpoint-example-outputs/program-context-examples/other-example-output",
                        "/video/GVD/gvd-endpoints/endpoint-example-outputs/program-context-examples/season-example-output",
                        "/video/GVD/gvd-endpoints/endpoint-example-outputs/program-context-examples/series-example-output",
                        "/video/GVD/gvd-endpoints/endpoint-example-outputs/program-context-examples/shows-example-output",
                        "/video/GVD/gvd-endpoints/endpoint-example-outputs/program-context-examples/sports-example-output",
                      ],
                    },
                    {
                      type: "category",
                      label: "Program Presentation Examples",
                      items: [
                        "/video/GVD/gvd-endpoints/endpoint-example-outputs/program-presentation-examples/program-presentation-example-output",
                      ],
                    },
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/source-example-output",
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/video-descriptors-program-context-example-output",
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/vod-catalog-item-example-output",
                    "/video/GVD/gvd-endpoints/endpoint-example-outputs/vod-source-example-output",
                  ],
                },
              ],
            },
            "/video/GVD/product-demonstration-kit-pdk",
            "/video/GVD/schemas-v3-0-5",
            "/video/GVD/xml-schema-files-xsds",
            "/video/GVD/data-dictionaries-and-controlled-vocabulary",
            "/video/GVD/release-notes-customer-notifications",
            "/video/GVD/glossary",

            {
              type: "category",
              label: "Using the Docs",
              items: [
                "/video/GVD/using-the-docs/doc-build-version",
                "/video/GVD/using-the-docs/copyright",
              ],
            },
            {
              type: "link",
              to: "/catalog/gvd",
              label: "API Documentation",
            },
          ],
        },
        {
          type: "category",
          label: "On-Connect Video/Social APIs",
          icon: "play-circle",
          items: [
            "/video/online-video-social-apis/index",
            "/video/online-video-social-apis/multiple-episodes",
            "/video/online-video-social-apis/single-episode",
            "/video/online-video-social-apis/single-movie",
            "/video/online-video-social-apis/single-series",
            "/video/online-video-social-apis/single-special",
            "/video/online-video-social-apis/social-data",
            {
              type: "category",
              label: "Media Cloud",
              items: [
                "/video/online-video-social-apis/media-cloud/media-cloud-landing-page",
                "/video/online-video-social-apis/media-cloud/single-image",
              ],
            },
            {
              type: "link",
              to: "/catalog/onconnect",
              label: "API Documentation",
            },
          ],
        },
        {
          type: "category",
          label: "On-Connect Lookup APIs",
          icon: "search",
          items: [
            "/video/on-connect-lookup-apis/index",
            {
              type: "category",
              label: "Celebrities",
              items: [
                "/video/on-connect-lookup-apis/celebrities/celebrities-landing-page",
                "/video/on-connect-lookup-apis/celebrities/celebrities-on-talk-shows-by-day",
                "/video/on-connect-lookup-apis/celebrities/celebrity-airings",
                "/video/on-connect-lookup-apis/celebrities/celebrity-details",
                "/video/on-connect-lookup-apis/celebrities/celebrity-images",
              ],
            },
            {
              type: "category",
              label: "Lineups",
              items: [
                "/video/on-connect-lookup-apis/international-lineups/lineups-landing-page",
                "/video/on-connect-lookup-apis/international-lineups/lineup-airings-tv-grid",
                "/video/on-connect-lookup-apis/international-lineups/lineup-channel-listing",
                "/video/on-connect-lookup-apis/international-lineups/lineup-details",
                "/video/on-connect-lookup-apis/international-lineups/lineups-by-postal-code",
              ],
            },
            {
              type: "category",
              label: "Movies",
              items: [
                "/video/on-connect-lookup-apis/movies/movies-landing-page",
                "/video/on-connect-lookup-apis/movies/future-releases",
                "/video/on-connect-lookup-apis/movies/movie-airings-on-tv",
                "/video/on-connect-lookup-apis/movies/movie-showtimes",
                "/video/on-connect-lookup-apis/movies/movie-versions",
                "/video/on-connect-lookup-apis/movies/movies-on-tv-by-day",
                "/video/on-connect-lookup-apis/movies/movies-playing-in-local-theatres",
                "/video/on-connect-lookup-apis/theatres/theatre-details",
                "/video/on-connect-lookup-apis/theatres/theatre-showtimes",
                "/video/on-connect-lookup-apis/theatres/theatres-by-postal-code",
              ],
            },
            {
              type: "category",
              label: "Programs",
              items: [
                "/video/on-connect-lookup-apis/programs/programs-landing-page",
                "/video/on-connect-lookup-apis/programs/advance-planner",
                "/video/on-connect-lookup-apis/programs/new-shows-aired-last-week",
                "/video/on-connect-lookup-apis/programs/new-shows-airing-by-day",
                "/video/on-connect-lookup-apis/programs/program-airings",
                "/video/on-connect-lookup-apis/programs/program-details",
                "/video/on-connect-lookup-apis/programs/program-genres",
                "/video/on-connect-lookup-apis/programs/program-images",
                "/video/on-connect-lookup-apis/programs/program-search",
              ],
            },
            {
              type: "category",
              label: "Series",
              items: [
                "/video/on-connect-lookup-apis/series/series-landing-page",
                "/video/on-connect-lookup-apis/series/series-airings",
                "/video/on-connect-lookup-apis/series/series-details",
                "/video/on-connect-lookup-apis/series/series-episodes",
              ],
            },
            {
              type: "category",
              label: "Sports",
              items: [
                "/video/on-connect-lookup-apis/sports/sports-landing-page",
                "/video/on-connect-lookup-apis/sports/organization-airings",
                "/video/on-connect-lookup-apis/sports/sports-events-airings",
                "/video/on-connect-lookup-apis/sports/sports-nonevents-airings",
                "/video/on-connect-lookup-apis/sports/sports-with-organizations",
                "/video/on-connect-lookup-apis/sports/team-airings",
                "/video/on-connect-lookup-apis/sports/team-details",
                "/video/on-connect-lookup-apis/sports/teams-by-organization",
                "/video/on-connect-lookup-apis/sports/teams-by-university",
                "/video/on-connect-lookup-apis/sports/universities",
              ],
            },
            {
              type: "category",
              label: "Stations",
              items: [
                "/video/on-connect-lookup-apis/stations/stations-landing-page",
                "/video/on-connect-lookup-apis/stations/stations-airings",
                "/video/on-connect-lookup-apis/stations/stations-details",
                "/video/on-connect-lookup-apis/stations/stations-search",
              ],
            },
            {
              type: "link",
              to: "/video/on-connect-lookup-apis/international-lineups/international-lineups",
              label: "International Lineups",
            },
            {
              type: "link",
              to: "/video/on-connect-lookup-apis/reference/reference",
              label: "Reference",
            },
            {
              type: "category",
              label: "Media Cloud",
              items: [
                "/video/on-connect-lookup-apis/media-cloud/media-cloud-landing-page",
                "/video/on-connect-lookup-apis/media-cloud/single-image",
              ],
            },
            {
              type: "category",
              label: "Metadata",
              items: [
                "/video/on-connect-lookup-apis/metadata/program-metadata",
                "/video/on-connect-lookup-apis/metadata/organizations-metadata",
                "/video/on-connect-lookup-apis/metadata/image-metadata",
                "/video/on-connect-lookup-apis/metadata/credits-metadata",
                "/video/on-connect-lookup-apis/metadata/awards-metadata",
              ],
            },
            {
              type: "link",
              to: "/catalog/onconnect",
              label: "API Documentation",
            },
          ],
        },
        {
          type: "category",
          label: "GN IDs API",
          icon: "fingerprint",
          items: [
            "/video/gn-ids-api/about-gn-ids-api",
            "/video/gn-ids-api/programs",
            "/video/gn-ids-api/publish-or-republish-a-program",
            "/video/gn-ids-api/images",
            "/video/gn-ids-api/catalogs",
            "/video/gn-ids-api/asset-information-and-status",
            "/video/gn-ids-api/data-dictionary-and-controlled-vocabulary",
            {
              type: "link",
              to: "https://nielsen113.zendesk.com/auth/v2/login/signin?return_to=https%3A%2F%2Fsupport.gracenote.com%2Fhc%2Fen-us%2Fsections%2F26781478142865-Gracenote-View&theme=hc&locale=en-us&brand_id=19094754412817&auth_origin=19094754412817%2Ctrue%2Ctrue",
              label: "Release Notes",
              target: "_blank",
            },
            "/video/gn-ids-api/copyright",
            {
              type: "link",
              to: "/catalog/gnids",
              label: "API Documentation",
            },
          ],
        },
        {
          type: "category",
          label: "GN IDs Web App",
          icon: "globe",
          items: [
            "/video/gn-ids-web-app/about-the-gn-ids-web-application",
            "/video/gn-ids-web-app/workflow",
            "/video/gn-ids-web-app/getting-started",
            "/video/gn-ids-web-app/view-programs",
            "/video/gn-ids-web-app/register-create-programs",
            "/video/gn-ids-web-app/publish-or-republish-programs",
            "/video/gn-ids-web-app/what-is-bulk-publish",
            "/video/gn-ids-web-app/delete-programs-seasons-and-episodes",
            "/video/gn-ids-web-app/images",
            "/video/gn-ids-web-app/catalogs",
            {
              type: "link",
              to: "https://nielsen113.zendesk.com/auth/v2/login/signin?return_to=https%3A%2F%2Fsupport.gracenote.com%2Fhc%2Fen-us%2Fsections%2F26781478142865-Gracenote-View&theme=hc&locale=en-us&brand_id=19094754412817&auth_origin=19094754412817%2Ctrue%2Ctrue",
              label: "Release Notes",
              target: "_blank",
            },
            "/video/gn-ids-web-app/copyright",
            {
              type: "link",
              to: "https://gracenoteview.com/?redirectPath=/",
              label: "GN IDs Web App",
              icon: "external-link",
            },
          ],
        },
        {
          type: "category",
          label: "GN View Web App",
          icon: "eye",
          items: [
            "/video/Gracenote-View/gracenote-view",
            "/video/Gracenote-View/login",
            "/video/Gracenote-View/programs",
            "/video/Gracenote-View/schedules",
            "/video/Gracenote-View/streaming-video-catalogs",
            "/video/Gracenote-View/video-descriptors",
            "/video/Gracenote-View/advanced-search",
            "/video/Gracenote-View/release-notes",
            "/video/Gracenote-View/copyright",
            {
              type: "link",
              to: "https://gracenoteview.com/?redirectPath=/",
              label: "GN View Web App",
              icon: "external-link",
            },
          ],
        },
        {
          type: "link",
          to: "/video/on-xml-info",
          label: "On XML (On TV)",
          icon: "file-text",
        },
      ],
    },
    {
      type: "category",
      label: "Audio",
      icon: "music",
      items: [
        "/audio/index",
        {
          type: "category",
          label: "GMD API v2",
          icon: "disc",
          items: [
            "/audio/gmd-api-v2/Overview",
            "/audio/gmd-api-v2/get-started",
            "/audio/gmd-api-v2/key-concepts-terms",
            "/audio/gmd-api-v2/best-practices",
            "/audio/gmd-api-v2/categories-language-values",
            "/audio/gmd-api-v2/error-codes",
            {
              type: "link",
              to: "/catalog/gmd-v2",
              label: "API Documentation",
            },
          ],
        },
        {
          type: "category",
          label: "GMD API v3",
          icon: "disc",
          items: [
            "/audio/gmd-api-v3/overview",
            "/audio/gmd-api-v3/get-started",
            "/audio/gmd-api-v3/key-concepts-and-terminology",
            "/audio/gmd-api-v3/api-reference",
            "/audio/gmd-api-v3/best-practices",
            "/audio/gmd-api-v3/data-types",
            "/audio/gmd-api-v3/descriptors",
            "/audio/gmd-api-v3/error-codes",
            {
              type: "link",
              to: "/catalog/gmd-v3",
              label: "API Documentation",
            },
          ],
        },
        {
          type: "category",
          label: "GMD Export",
          icon: "download",
          items: [
            "/audio/gmd-export/gmd",
            "/audio/gmd-export/terminology",
            "/audio/gmd-export/basics",
            "/audio/gmd-export/use-cases",
            "/audio/gmd-export/objects",
            "/audio/gmd-export/data-model",
            "/audio/gmd-export/import-to-db",
            "/audio/gmd-export/msa",
            "/audio/gmd-export/process-batch-files",
          ],
        },
        {
          type: "link",
          to: "https://gracenote.flexnetoperations.com/control/grac/login?nextURL=%2Fcontrol%2Fgrac%2Findex",
          label: "GNSDK for Mobile",
          icon: "smartphone",
          target: "_blank",
        },
        {
          type: "link",
          to: "https://gracenote.flexnetoperations.com/control/grac/login?nextURL=%2Fcontrol%2Fgrac%2Findex",
          label: "GNSDK for Desktop",
          icon: "monitor",
          target: "_blank",
        },
      ],
    },
    {
      type: "category",
      label: "Sports",
      icon: "trophy",
      items: [
        "/sports/index",
        {
          type: "category",
          label: "Global Sports Data API",
          icon: "database",
          items: [
            "/sports/Global-Sports-Data/global-sports-data-overview",
            {
              type: "link",
              to: "/catalog/global-sports-data",
              label: "API Documentation",
            },
          ],
        },
        {
          type: "category",
          label: "Global Sports Connected API",
          icon: "link",
          items: [
            "/sports/Global-Sports-Data/global-sports-connected-overview",
            {
              type: "link",
              to: "/catalog/global-sports-connected",
              label: "API Documentation",
            },
          ],
        },
        {
          type: "link",
          to: "https://gsw.sports.gracenote.com/documentation/",
          label: "Global Sports Widgets",
          icon: "layout-grid",
        },
        {
          type: "link",
          to: "/video/on-api/sports-endpoint",
          label: "On Sports",
          icon: "play-circle",
        },
      ],
    },
    {
      type: "category",
      label: "Automotive",
      icon: "car",
      items: [
        "/automotive/index",
        {
          type: "link",
          to: "/automotive/nexus-auto/nexus-api-protected",
          label: "Nexus Auto API",
          icon: "radio",
        },
        {
          type: "link",
          to: "/automotive/nexus-auto/nexus-android-protected",
          label: "Nexus Auto Android Guide",
          icon: "smartphone",
        },
        {
          type: "link",
          to: "/automotive/nexus-auto/nexus-sdk-protected",
          label: "Nexus Auto SDK",
          icon: "code",
        },
        {
          type: "link",
          to: "/automotive/rsid-protected",
          label: "RSID API",
          icon: "antenna",
        },
        {
          type: "link",
          to: "https://gracenote.flexnetoperations.com/control/grac/login?nextURL=%2Fcontrol%2Fgrac%2Findex",
          label: "Automotive SDK (GNSDK)",
          icon: "package",
          target: "_blank",
        },
      ],
    },

    {
      type: "link",
      icon: "square-library",
      to: "/catalog",
      label: "API Catalog",
    },
    {
      type: "link",
      to: "/contact-us",
      label: "Contact Us",
      icon: "mail",
    },
    {
      type: "link",
      to: "https://support.gracenote.com/hc/en-us",
      label: "Help Center",
      icon: "help-circle",
      target: "_blank",
    },
  ],
  
  // --- CATALOG CONFIGURATION ---
  catalogs: {
    path: "catalog",
    label: "API Catalog",
  },

  // --- URL REDIRECTS ---
  redirects: [
    { from: "/video", to: "/video/index" },
    { from: "/audio", to: "/audio/index" },
    { from: "/sports", to: "/sports/index" },
    { from: "/automotive", to: "/automotive/index" },
    { from: "/sports/Global-Sports-Data/Global Sports Data API/Global-sports-data-overview", to: "/sports/Global-Sports-Data/global-sports-data-overview" },
    { from: "/sports/Global-Sports-Data/Global Sports Connected API/Global-sports-connected-overview", to: "/sports/Global-Sports-Data/global-sports-connected-overview" },
    { from: "/video/gn-ids/about-gn-ids-api", to: "/video/gn-ids-api/about-gn-ids-api" },
    { from: "/video/gn-ids/programs", to: "/video/gn-ids-api/programs" },
    { from: "/video/gn-ids/publish-or-republish-a-program", to: "/video/gn-ids-api/publish-or-republish-a-program" },
    { from: "/video/gn-ids/images", to: "/video/gn-ids-api/images" },
    { from: "/video/gn-ids/catalogs", to: "/video/gn-ids-api/catalogs" },
    { from: "/video/gn-ids/asset-information-and-status", to: "/video/gn-ids-api/asset-information-and-status" },
    { from: "/video/gn-ids/data-dictionary-and-controlled-vocabulary", to: "/video/gn-ids-api/data-dictionary-and-controlled-vocabulary" },
    { from: "/video/gn-ids/release-notes", to: "/video/gn-ids-api/release-notes" },
    { from: "/video/gn-ids/copyright", to: "/video/gn-ids-api/copyright" },
    { from: "/video/On-Connect/onconnect-lookup-apis", to: "/video/On-Connect/onconnect-api-overview" },
    { from: "/video/On-Connect/online-video-and-social-apis", to: "/video/On-Connect/onconnect-api-overview" },
    { from: "/video/On-Connect/media-cloud", to: "/video/On-Connect/onconnect-api-overview" },
    // API Catalog redirects
    { from: "/catalog/nexus", to: "/automotive/nexus-protected" },
    { from: "/catalog/rsid-api", to: "/automotive/rsid-protected" },

  ],
  
  // --- API DOCUMENTATION GENERATION ---
  apis: [
    {
      type: "file",
      input: "./apis/gnids.yaml",
      path: "/catalog/gnids",
      categories: [{ label: "Video Data", tags: ["GN IDs"] }],
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/gnids",
      },
    },
    {
      type: "file",
      input: "./apis/gmdv3.yaml",
      path: "/catalog/gmd-v3",
      categories: [{ label: "Audio Data", tags: ["GMD API v3"] }],
      auth: {
        type: "apiKey",
        in: "header",
        name: "GN-APIKEY"
      },
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/gmd",
      },
    },

    {
      type: "file",
      input: "./apis/gmdv2.yaml",
      path: "/catalog/gmd-v2",
      categories: [{ label: "Audio Data", tags: ["GMD API v2"] }],
      auth: {
        type: "apiKey",
        in: "header",
        name: "GN-APIKEY"
      },
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/gmd",
      },
    },

    {
      type: "file",
      input: "./apis/nexus.yaml",
      path: "/catalog/nexus-hidden",
      skipIndex: true,
      categories: [],
      auth: {
        type: "apiKey",
        in: "header",
        name: "GN-APIKEY"
      },
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/nexus",
      },
    },
    {
      type: "file",
      input: "./apis/rsid-api.yaml",
      path: "/catalog/rsid-hidden",
      skipIndex: true,
      categories: [],
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/rsid",
      },
    },
    {
      type: "file",
      input: "./apis/gsapi-openapi.json",
      path: "/catalog/global-sports",
      categories: [{ label: "Sports Data", tags: ["Global Sports API"] }],
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/sports",
      },
    },
    {
      type: "file",
      input: "./apis/gvd-openapi.json",
      path: "/catalog/gvd",
      categories: [{ label: "Video Data", tags: ["GVD API"] }],
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/gvd",
      },
    },
    {
      type: "file",
      input: "./apis/onapi-openapi.json",
      path: "/catalog/on-api",
      categories: [{ label: "Video Data", tags: ["On API"] }],
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/on-api",
      },
    },
    {
      type: "file",
      input: "./apis/gsc-openapi.json",
      path: "/catalog/global-sports-connected",
      categories: [{ label: "Sports Data", tags: ["Global Sports Connected"] }],
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/sports",
      },
    },
    {
      type: "file",
      input: "./apis/gsd-openapi.json",
      path: "/catalog/global-sports-data",
      categories: [{ label: "Sports Data", tags: ["Global Sports Data"] }],
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/sports",
      },
    },
    {
      type: "file",
      input: "./apis/onconnect-openapi.json",
      path: "/catalog/onconnect",
      categories: [{ label: "Video Data", tags: ["OnConnect Lookup APIs"] }],
      options: {
        hideInfo: true,
        hideNotes: true,
        hideWarnings: true,
        hideGeneratedContent: true,
      },
      playground: {
        enabled: true,
        proxy: "/proxy/onconnect",
      },
    },

  ],
  

};

export default config;